import { ref, onMounted, onUnmounted, type Ref } from "vue";

export interface ScrollDirectionOptions {
  /** Threshold in pixels before direction change is detected */
  threshold?: number;
  /** Element to track scroll on (defaults to window) */
  element?: Ref<HTMLElement | null>;
}

export interface ScrollDirectionReturn {
  /** Current scroll direction: 'up', 'down', or null (at top) */
  scrollDirection: Ref<"up" | "down" | null>;
  /** Whether the user is at the top of the page */
  isAtTop: Ref<boolean>;
  /** Current scroll position */
  scrollY: Ref<number>;
  /** Whether header should be visible based on scroll behavior */
  isHeaderVisible: Ref<boolean>;
}

export function useScrollDirection(
  options: ScrollDirectionOptions = {}
): ScrollDirectionReturn {
  const { threshold = 10, element } = options;

  const scrollDirection = ref<"up" | "down" | null>(null);
  const isAtTop = ref(true);
  const scrollY = ref(0);
  const isHeaderVisible = ref(true);

  let lastScrollY = 0;
  let ticking = false;

  // Helper to get the actual scroll position (works on mobile iOS/Android)
  const getScrollTop = (): number => {
    // If element is provided, use it
    if (element?.value) {
      return element.value.scrollTop;
    }
    
    // On mobile, check for content-wrapper or app-layout__center first
    const contentWrapper = document.querySelector('.content-wrapper') as HTMLElement;
    if (contentWrapper) {
      return contentWrapper.scrollTop;
    }
    
    // Check app-layout__center
    const appLayoutCenter = document.querySelector('.app-layout__center') as HTMLElement;
    if (appLayoutCenter) {
      return appLayoutCenter.scrollTop;
    }
    
    // Fallback to window scroll (desktop)
    return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
  };

  function updateScrollDirection() {
    const currentScrollY = getScrollTop();

    scrollY.value = currentScrollY;
    isAtTop.value = currentScrollY < 50;

    // Always show header at top of page
    if (currentScrollY < 50) {
      isHeaderVisible.value = true;
      scrollDirection.value = null;
      lastScrollY = currentScrollY;
      ticking = false;
      return;
    }

    const diff = currentScrollY - lastScrollY;

    // Only trigger if scroll exceeds threshold
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Scrolling down
        scrollDirection.value = "down";
        isHeaderVisible.value = false;
      } else {
        // Scrolling up
        scrollDirection.value = "up";
        isHeaderVisible.value = true;
      }
      lastScrollY = currentScrollY;
    }

    ticking = false;
  }

  function handleScroll() {
    if (!ticking) {
      window.requestAnimationFrame(updateScrollDirection);
      ticking = true;
    }
  }

  onMounted(() => {
    // If element is provided, use it
    if (element?.value) {
      element.value.addEventListener("scroll", handleScroll, { passive: true });
    } else {
      // Listen to window and mobile scroll containers
      window.addEventListener("scroll", handleScroll, { passive: true });
      
      // Also listen to mobile scroll containers
      const contentWrapper = document.querySelector('.content-wrapper');
      const appLayoutCenter = document.querySelector('.app-layout__center');
      
      if (contentWrapper) {
        contentWrapper.addEventListener("scroll", handleScroll, { passive: true });
      }
      if (appLayoutCenter) {
        appLayoutCenter.addEventListener("scroll", handleScroll, { passive: true });
      }
    }

    // Initial check
    updateScrollDirection();
  });

  onUnmounted(() => {
    // If element is provided, remove from it
    if (element?.value) {
      element.value.removeEventListener("scroll", handleScroll);
    } else {
      // Remove from window and mobile scroll containers
      window.removeEventListener("scroll", handleScroll);
      
      const contentWrapper = document.querySelector('.content-wrapper');
      const appLayoutCenter = document.querySelector('.app-layout__center');
      
      if (contentWrapper) {
        contentWrapper.removeEventListener("scroll", handleScroll);
      }
      if (appLayoutCenter) {
        appLayoutCenter.removeEventListener("scroll", handleScroll);
      }
    }
  });

  return {
    scrollDirection,
    isAtTop,
    scrollY,
    isHeaderVisible,
  };
}
